import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Mission", href: "#mission" },
  { label: "Services", href: "#services" },
  { label: "Process", href: "#process" },
  { label: "Hospitality & Advisory", href: "/hospitality-advisory" },
  { label: "Pricing", href: "#pricing" },
  { label: "Contact", href: "#contact" },
];

const linkId = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, "-");

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="nav-container"
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-[hsl(40_33%_98%/0.72)] backdrop-blur-xl border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <nav className="container-luxe flex h-[72px] items-center justify-between">
        <a
          href="#top"
          data-testid="nav-logo"
          className="flex items-baseline gap-2"
        >
          <span className="serif text-xl sm:text-2xl tracking-tight text-[hsl(var(--foreground))]">
            Beam
          </span>
          <span className="serif text-xl sm:text-2xl tracking-tight gold-shimmer">
            Concierge
          </span>
        </a>

        <div className="hidden lg:flex items-center gap-7 xl:gap-9">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${linkId(l.label)}`}
              className="link-underline text-sm text-muted-foreground hover:text-foreground transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href="#contact"
            data-testid="nav-cta"
            className="group inline-flex items-center gap-2 bg-secondary px-5 py-2.5 text-sm text-secondary-foreground hover:bg-[var(--gold)] transition-colors duration-400 whitespace-nowrap"
          >
            Book an Operations Audit Call
          </a>
        </div>

        <button
          data-testid="nav-mobile-toggle"
          className="lg:hidden inline-flex h-10 w-10 items-center justify-center text-foreground"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="lg:hidden overflow-hidden bg-[hsl(40_33%_98%/0.96)] backdrop-blur-xl border-b border-border"
          >
            <div className="container-luxe flex flex-col py-6 gap-1">
              {LINKS.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-link-${linkId(l.label)}`}
                  className="serif text-2xl py-2 text-foreground"
                >
                  {l.label}
                </a>
              ))}
              <a
                href="#contact"
                onClick={() => setOpen(false)}
                className="mt-4 inline-flex items-center justify-center bg-secondary px-5 py-3 text-sm text-secondary-foreground"
              >
                Book an Operations Audit Call
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
